/**
 * ImpersonationContext
 *
 * Permite que um super-admin troque a sessão Supabase atual pela sessão de
 * outro usuário (alvo) por até 1 hora, para testar a aplicação como ele.
 *
 * Fluxo:
 * 1. enterAs({ user_id }) salva tokens da sessão atual em localStorage e chama
 *    a Edge Function admin-impersonate-user para gerar um token_hash do alvo.
 * 2. supabase.auth.verifyOtp consome o token_hash e instala a sessão do alvo
 *    no localStorage do Supabase JS (autoRefreshToken cuida do resto).
 * 3. queryClient.clear() + remoção de current_org_id força os hooks a reler
 *    sob a nova identidade.
 * 4. Reload para `/` para garantir que toda a árvore de providers reinicialize
 *    com a nova sessão.
 *
 * Saída:
 * - exit() restaura a sessão admin via supabase.auth.setSession({ access, refresh })
 *   e redireciona para /super-admin/users.
 * - Auto-exit dispara quando expiresAt é alcançado (TTL de 1 hora).
 *
 * Persistência: chave `mengage_impersonation` em localStorage, sobrevive a F5.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'mengage_impersonation';
const TTL_MS = 60 * 60 * 1000; // 1 hora

interface ImpersonationTarget {
  user_id: string;
  email: string;
  full_name: string | null;
}

interface ImpersonationOriginSession {
  access_token: string;
  refresh_token: string;
}

export interface ImpersonationState {
  target: ImpersonationTarget;
  origin: ImpersonationOriginSession;
  startedAt: number;
  expiresAt: number;
}

function readState(): ImpersonationState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationState;
    if (
      !parsed?.target?.user_id ||
      !parsed?.origin?.access_token ||
      !parsed?.origin?.refresh_token ||
      typeof parsed.expiresAt !== 'number'
    ) {
      return null;
    }
    return parsed;
  } catch (err) {
    console.error('[Impersonation] failed to parse state', err);
    return null;
  }
}

function writeState(state: ImpersonationState | null) {
  if (state) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

interface EnterArgs {
  user_id: string;
}

interface ImpersonationContextValue {
  state: ImpersonationState | null;
  isImpersonating: boolean;
  enterAs: (args: EnterArgs) => Promise<void>;
  exit: () => Promise<void>;
  /** Recomputado pelo banner (re-render a cada 30s). */
  remainingMs: number;
  /** Estado de loading durante enterAs/exit (para desabilitar botão e exibir spinner). */
  busy: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ImpersonationState | null>(() => readState());
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState<number>(() => Date.now());
  const autoExitTimerRef = useRef<number | null>(null);

  // Tick a cada 30s para o banner exibir tempo restante atualizado.
  useEffect(() => {
    if (!state) return;
    const interval = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(interval);
  }, [state]);

  const exit = useCallback(async () => {
    const current = readState();
    if (!current) {
      setState(null);
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.setSession({
        access_token: current.origin.access_token,
        refresh_token: current.origin.refresh_token,
      });
      if (error) {
        console.error('[Impersonation] exit setSession failed', error);
        // Mesmo com erro, limpar estado e forçar relogin.
        writeState(null);
        setState(null);
        try { queryClient.clear(); } catch (e) { console.warn(e); }
        localStorage.removeItem('current_org_id');
        await supabase.auth.signOut().catch(() => {});
        window.location.assign('/auth');
        return;
      }
      writeState(null);
      setState(null);
      try { queryClient.clear(); } catch (e) { console.warn(e); }
      localStorage.removeItem('current_org_id');
      // Reload pra garantir reinicialização completa de todos os providers.
      window.location.assign('/super-admin/users');
    } finally {
      setBusy(false);
    }
  }, [queryClient]);

  // Auto-exit no TTL.
  useEffect(() => {
    if (autoExitTimerRef.current) {
      window.clearTimeout(autoExitTimerRef.current);
      autoExitTimerRef.current = null;
    }
    if (!state) return;
    const remaining = state.expiresAt - Date.now();
    if (remaining <= 0) {
      // Já expirou — saída imediata.
      void exit();
      return;
    }
    autoExitTimerRef.current = window.setTimeout(() => {
      void exit();
    }, remaining);
    return () => {
      if (autoExitTimerRef.current) {
        window.clearTimeout(autoExitTimerRef.current);
        autoExitTimerRef.current = null;
      }
    };
  }, [state, exit]);

  const enterAs = useCallback(
    async ({ user_id }: EnterArgs) => {
      setBusy(true);
      try {
        // 1. Captura sessão admin atual.
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !sessionData.session) {
          throw new Error('Sessão admin não encontrada — relogue antes de impersonar.');
        }
        const origin: ImpersonationOriginSession = {
          access_token: sessionData.session.access_token,
          refresh_token: sessionData.session.refresh_token,
        };

        // 2. Pede token de impersonation à Edge Function.
        const { data: fnData, error: fnError } = await supabase.functions.invoke<{
          token_hash: string;
          email: string;
          target_user_id: string;
          full_name: string | null;
        }>('admin-impersonate-user', {
          body: { target_user_id: user_id },
        });
        if (fnError || !fnData?.token_hash || !fnData.email) {
          throw new Error(fnError?.message || 'Falha ao gerar token de impersonation.');
        }

        // 3. Persiste estado ANTES do verifyOtp (verifyOtp dispara onAuthStateChange,
        //    que pode disparar lógicas dependentes de localStorage).
        const target: ImpersonationTarget = {
          user_id: fnData.target_user_id,
          email: fnData.email,
          full_name: fnData.full_name,
        };
        const startedAt = Date.now();
        const newState: ImpersonationState = {
          target,
          origin,
          startedAt,
          expiresAt: startedAt + TTL_MS,
        };
        writeState(newState);

        // 4. Troca a sessão Supabase para o alvo.
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: fnData.token_hash,
          type: 'magiclink',
        });
        if (verifyError) {
          // Reverte estado.
          writeState(null);
          throw new Error(`Falha ao trocar sessão: ${verifyError.message}`);
        }

        // 5. Limpa caches e current_org_id (alvo pode ter outras orgs).
        try { queryClient.clear(); } catch (e) { console.warn(e); }
        localStorage.removeItem('current_org_id');

        // 6. Atualiza estado local e força reload.
        setState(newState);
        window.location.assign('/');
      } catch (err) {
        console.error('[Impersonation] enterAs failed', err);
        throw err;
      } finally {
        setBusy(false);
      }
    },
    [queryClient],
  );

  const remainingMs = useMemo(() => {
    if (!state) return 0;
    return Math.max(0, state.expiresAt - now);
  }, [state, now]);

  const value = useMemo<ImpersonationContextValue>(
    () => ({
      state,
      isImpersonating: !!state,
      enterAs,
      exit,
      remainingMs,
      busy,
    }),
    [state, enterAs, exit, remainingMs, busy],
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return ctx;
}
